import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { PdpSharedSimilarSoldItemsColumn } from './PdpSharedSimilarSoldItemsColumn.tsx';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { useShouldShowSimilarSoldItems } from '../hooks/useShouldShowSimilarSoldItems.ts';
import { useDetailEntries } from './useDetailEntries.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import styles from './PdpSharedSimilarSoldItemsColumns.module.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpSharedSimilarSoldItemsColumns_item$key } from './__generated__/PdpSharedSimilarSoldItemsColumns_item.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumns_viewer$key } from './__generated__/PdpSharedSimilarSoldItemsColumns_viewer.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_user$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_user.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumns_item on Item {
        ...PdpSharedSimilarSoldItemsColumn_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                catchNonFatalErrors: false
            )
        ...useDetailEntries_item
        ...useShouldShowSimilarSoldItems_item
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumns_viewer on Viewer {
        ...PdpSharedSimilarSoldItemsColumn_viewer
        ...useShouldShowSimilarSoldItems_viewer
        similarSoldItemsConnection(itemId: $itemId, first: 4, requestSourceRole: BUYER) {
            edges {
                node {
                    itemId
                    soldDate
                    item {
                        ...PdpSharedSimilarSoldItemsColumn_item
                            @arguments(
                                userId: $userId
                                isTrade: $isTrade
                                userCountryCode: $userCountryCode
                                priceBookName: $priceBookName
                                catchNonFatalErrors: false
                            )
                    }
                }
            }
        }
    }
`;

type PdpSharedSimilarSoldItemsColumnsProps = {
    item: PdpSharedSimilarSoldItemsColumns_item$key;
    viewer: PdpSharedSimilarSoldItemsColumns_viewer$key;
    user?: PdpSharedSimilarSoldItemsColumn_user$key | null;
    currency: string;
    inModal?: boolean;
};

export const PdpSharedSimilarSoldItemsColumns: FC<PdpSharedSimilarSoldItemsColumnsProps> = ({
    item: itemRef,
    viewer: viewerRef,
    user,
    currency,
    inModal,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});
    const { isBelowMinPriceEstimate, shouldShowEstimateValueBadge } = useShouldShowSimilarSoldItems(
        {
            viewer,
            item,
            currency,
            afterDisplay,
        }
    );
    const detailEntries = useDetailEntries({ item });
    const similarSoldItems =
        viewer?.similarSoldItemsConnection?.edges
            ?.map(edge => edge?.node || null)
            ?.filter(filterNulls) ?? [];

    return (
        <div
            style={{
                ['--grid-rows' as string]: 3 + detailEntries.length,
                ['--grid-cols' as string]: similarSoldItems.length,
            }}
            className={classnames(styles.gridContainer, dibsCss.grid, dibsCss.wFit, {
                [dibsCss.mlSmall]: inModal,
                [dibsCss.mrXsmall]: inModal,
            })}
        >
            <PdpSharedSimilarSoldItemsColumn
                viewer={viewer}
                item={item}
                user={user}
                isCurrentItem
                currency={currency}
                detailEntries={detailEntries}
                inModal={inModal}
                isBelowMinPriceEstimate={isBelowMinPriceEstimate}
                shouldShowEstimateValueBadge={shouldShowEstimateValueBadge}
            />
            {similarSoldItems.map(({ itemId, item: similarSoldItem, soldDate }, index) => (
                <PdpSharedSimilarSoldItemsColumn
                    key={itemId}
                    viewer={viewer}
                    item={similarSoldItem}
                    user={user}
                    itemSoldDate={soldDate}
                    currency={currency}
                    detailEntries={detailEntries}
                    isLastItem={index === similarSoldItems.length - 1}
                />
            ))}
        </div>
    );
};
